import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2,
  Plug,
  PlugZap,
  ShieldAlert,
  Power,
  RefreshCcw,
  Eye,
  EyeOff,
  Trash2,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const MODES = [
  {
    id: "paper",
    label: "Paper",
    desc: "Real market engine, simulated money. Zero risk — great for testing.",
  },
  {
    id: "live",
    label: "Live",
    desc: "Real money. Real orders hit the market in your name.",
  },
];

export default function Trading() {
  const { refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/brokerage/status");
      setStatus(data);
      const activeConnected = data.connections?.[data.real_trading_mode];
      if (activeConnected) {
        const [acc, pos] = await Promise.allSettled([
          api.get("/brokerage/account"),
          api.get("/brokerage/positions"),
        ]);
        setAccount(acc.status === "fulfilled" ? acc.value.data : null);
        setPositions(pos.status === "fulfilled" ? pos.value.data.positions : []);
      } else {
        setAccount(null);
        setPositions([]);
      }
    } catch (e) {
      toast.error("Failed to load brokerage status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const connect = async (mode, apiKey, apiSecret) => {
    setBusy(true);
    try {
      const { data } = await api.post("/brokerage/connect", {
        api_key: apiKey,
        api_secret: apiSecret,
        mode,
      });
      toast.success(`Connected Alpaca ${mode}`, {
        description: `Buying power ${data.buying_power}`,
      });
      await load();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async (mode) => {
    setBusy(true);
    try {
      await api.delete(`/brokerage/connect/${mode}`);
      toast.success(`Disconnected Alpaca ${mode}`);
      await load();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleReal = async (enabled, mode) => {
    if (enabled && mode === "live") {
      const ok = window.confirm(
        "Enable LIVE real-money trading? Orders will execute with real funds in your Alpaca account.",
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      await api.post("/brokerage/real-trading", { enabled, mode });
      toast.success(
        enabled ? `Real trading ON · ${mode}` : "Real trading OFF · using paper wallet",
      );
      await load();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change mode");
    } finally {
      setBusy(false);
    }
  };

  const realOn = status?.real_trading_enabled;
  const activeMode = status?.real_trading_mode || "paper";

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="trading-page">
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            // trading · brokerage
          </div>
          <h1 className="mt-2 text-4xl md:text-5xl font-black tracking-tighter uppercase">
            Real Trading
          </h1>
          <p className="mt-3 text-sm text-neutral-400 max-w-2xl">
            Connect your own Alpaca brokerage account to trade real markets. When real
            trading is <span className="text-white">ON</span>, your buy/sell orders route to
            Alpaca instead of the internal paper wallet. Start in{" "}
            <span className="text-white">paper</span> mode — flip to live anytime by connecting a
            funded live account.
          </p>
        </div>
        <button
          data-testid="trading-refresh"
          onClick={load}
          className="border border-neo-border px-4 py-3 font-mono uppercase tracking-widest text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
        >
          <RefreshCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
          Refresh
        </button>
      </header>

      {loading ? (
        <div className="p-10 text-center text-neutral-500 font-mono text-xs uppercase tracking-widest">
          Loading…
        </div>
      ) : (
        <div className="space-y-6">
          {/* Master status / toggle */}
          <section
            className={`border bg-neo-surface p-6 ${
              realOn
                ? activeMode === "live"
                  ? "border-[#FF3366]/50"
                  : "border-[#00FF66]/50"
                : "border-neo-border"
            }`}
            data-testid="trading-master-status"
          >
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div
                  className={`w-11 h-11 border flex items-center justify-center ${
                    realOn
                      ? activeMode === "live"
                        ? "border-[#FF3366] text-[#FF3366]"
                        : "border-[#00FF66] text-[#00FF66]"
                      : "border-neo-border text-neutral-400"
                  }`}
                >
                  <Power className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                    execution mode
                  </div>
                  <div
                    className="mt-1 text-xl font-black tracking-tight uppercase"
                    data-testid="trading-mode-label"
                  >
                    {realOn ? `Alpaca ${activeMode} — real` : "Paper wallet — simulated"}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {realOn ? (
                  <button
                    data-testid="trading-disable-real"
                    disabled={busy}
                    onClick={() => toggleReal(false, activeMode)}
                    className="border border-neo-border px-5 py-3 font-mono uppercase tracking-widest text-xs hover:bg-white/5 transition-colors disabled:opacity-40"
                  >
                    Turn off real trading
                  </button>
                ) : (
                  MODES.map((m) => (
                    <button
                      key={m.id}
                      data-testid={`trading-enable-${m.id}`}
                      disabled={busy || !status?.connections?.[m.id]}
                      onClick={() => toggleReal(true, m.id)}
                      className={`px-5 py-3 font-mono uppercase tracking-widest text-xs border transition-colors disabled:opacity-30 ${
                        m.id === "live"
                          ? "border-[#FF3366] text-[#FF3366] hover:bg-[#FF3366]/10"
                          : "border-[#00FF66] text-[#00FF66] hover:bg-[#00FF66]/10"
                      }`}
                    >
                      Go real · {m.label}
                    </button>
                  ))
                )}
              </div>
            </div>

            {realOn && activeMode === "live" && (
              <div className="mt-4 flex items-start gap-3 border border-[#FF3366]/30 bg-[#FF3366]/5 px-4 py-3 text-xs text-[#FF3366]/90 font-mono leading-relaxed">
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                <span>
                  LIVE mode — every trade uses real money in your Alpaca account. Compliance,
                  funding and suitability are your responsibility as the account holder.
                </span>
              </div>
            )}
          </section>

          {/* Account summary (active mode) */}
          {account && (
            <section className="border border-neo-border bg-neo-surface" data-testid="trading-account">
              <div className="px-5 py-3 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Alpaca account · {account.mode} · {account.status}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-neo-border">
                <Stat label="Buying power" value={`$${account.buying_power}`} testid="trading-buying-power" />
                <Stat label="Cash" value={`$${account.cash}`} testid="trading-cash" />
                <Stat label="Equity" value={`$${account.equity}`} testid="trading-equity" />
                <Stat label="Currency" value={account.currency} testid="trading-currency" />
              </div>
            </section>
          )}

          {/* Positions */}
          {account && (
            <section className="border border-neo-border bg-neo-surface" data-testid="trading-positions">
              <div className="px-5 py-3 border-b border-neo-border font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                Live positions ({positions.length})
              </div>
              {positions.length === 0 ? (
                <div className="p-6 text-center text-neutral-500 font-mono text-xs">
                  No open positions
                </div>
              ) : (
                <div className="divide-y divide-neo-border font-mono text-sm">
                  <div className="grid grid-cols-12 gap-2 px-5 py-2 text-[10px] uppercase tracking-widest text-neutral-500">
                    <div className="col-span-3">Symbol</div>
                    <div className="col-span-2 text-right">Qty</div>
                    <div className="col-span-3 text-right">Market value</div>
                    <div className="col-span-4 text-right">Unrealized P/L</div>
                  </div>
                  {positions.map((p) => {
                    const pl = parseFloat(p.unrealized_pl || 0);
                    return (
                      <div
                        key={p.symbol}
                        className="grid grid-cols-12 gap-2 px-5 py-3 items-center"
                        data-testid={`trading-position-${p.symbol}`}
                      >
                        <div className="col-span-3 text-white uppercase">{p.symbol}</div>
                        <div className="col-span-2 text-right">{p.qty}</div>
                        <div className="col-span-3 text-right">${parseFloat(p.market_value || 0).toFixed(2)}</div>
                        <div className={`col-span-4 text-right ${pl >= 0 ? "pos" : "neg"}`}>
                          {pl >= 0 ? "+" : ""}
                          {pl.toFixed(2)} ({(parseFloat(p.unrealized_plpc || 0) * 100).toFixed(2)}%)
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}

          {/* Connections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {MODES.map((m) => (
              <ConnectionCard
                key={m.id}
                mode={m}
                connected={status?.connections?.[m.id]}
                accountId={status?.accounts?.[m.id]?.account_id}
                busy={busy}
                onConnect={connect}
                onDisconnect={disconnect}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="px-5 py-4" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">{label}</div>
      <div className="mt-1 font-mono text-lg font-bold text-white break-all">{value}</div>
    </div>
  );
}

function ConnectionCard({ mode, connected, accountId, busy, onConnect, onDisconnect }) {
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState("");
  const [reveal, setReveal] = useState(false);
  const isLive = mode.id === "live";

  return (
    <section
      className={`border bg-neo-surface ${connected ? (isLive ? "border-[#FF3366]/40" : "border-[#00FF66]/40") : "border-neo-border"}`}
      data-testid={`connection-card-${mode.id}`}
    >
      <div className="flex items-center justify-between p-5 border-b border-neo-border">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            alpaca · {mode.label}
          </div>
          <div className="mt-1 text-lg font-bold tracking-tight">
            {mode.label} account
          </div>
          <div className="mt-1 text-xs text-neutral-500 max-w-xs">{mode.desc}</div>
        </div>
        <div
          className={`inline-flex items-center gap-1.5 px-2 py-1 border font-mono text-[10px] uppercase tracking-widest ${
            connected
              ? isLive
                ? "border-[#FF3366]/50 text-[#FF3366]"
                : "border-[#00FF66]/50 text-[#00FF66]"
              : "border-neo-border text-neutral-500"
          }`}
          data-testid={`connection-badge-${mode.id}`}
        >
          {connected ? <PlugZap className="w-3 h-3" /> : <Plug className="w-3 h-3" />}
          {connected ? "connected" : "not connected"}
        </div>
      </div>

      <div className="p-5">
        {connected ? (
          <div className="flex items-center justify-between gap-4">
            <div className="font-mono text-xs text-neutral-400 break-all">
              Account ID: <span className="text-white">{accountId}</span>
            </div>
            <button
              data-testid={`connection-disconnect-${mode.id}`}
              disabled={busy}
              onClick={() => onDisconnect(mode.id)}
              className="border border-neo-border px-3 py-2 font-mono uppercase tracking-widest text-[10px] hover:bg-white/5 transition-colors flex items-center gap-2 disabled:opacity-40"
            >
              <Trash2 className="w-3 h-3" />
              Disconnect
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                API key ID
              </label>
              <input
                data-testid={`connection-key-${mode.id}`}
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="PK..."
                className="mt-1.5 w-full bg-transparent border border-neo-border px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-white transition-colors"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                  API secret
                </label>
                <button
                  type="button"
                  onClick={() => setReveal((v) => !v)}
                  className="inline-flex items-center gap-1 text-neutral-500 hover:text-white text-[10px] font-mono uppercase tracking-widest transition-colors"
                  data-testid={`connection-reveal-${mode.id}`}
                >
                  {reveal ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {reveal ? "hide" : "show"}
                </button>
              </div>
              <input
                data-testid={`connection-secret-${mode.id}`}
                type={reveal ? "text" : "password"}
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder="Paste your Alpaca secret"
                className="mt-1.5 w-full bg-transparent border border-neo-border px-3 py-2.5 font-mono text-sm focus:outline-none focus:border-white transition-colors"
              />
            </div>
            <button
              data-testid={`connection-connect-${mode.id}`}
              disabled={busy || key.length < 8 || secret.length < 8}
              onClick={() => onConnect(mode.id, key, secret)}
              className={`w-full py-3 font-mono uppercase tracking-widest text-xs border transition-colors disabled:opacity-40 flex items-center justify-center gap-2 ${
                isLive
                  ? "border-[#FF3366] text-[#FF3366] hover:bg-[#FF3366]/10"
                  : "bg-white text-black border-white hover-invert"
              }`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plug className="w-3.5 h-3.5" />}
              Connect {mode.label}
            </button>
            <p className="text-[10px] font-mono text-neutral-600 leading-relaxed">
              Generate keys at app.alpaca.markets → your {mode.label} account → API keys. The
              secret is shown once — paste it here. Keys are encrypted on our server and never
              exposed to the browser again.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
